// Shared row shapes for the lazy external-data page. Keep this file independent
// of the page so shell component registration does not load the route body.
// makeRemoteUrl resolves the canonical empty-selection path through the existing
// function registry; handlers therefore need no page-module closure.

// ── Rail row — one workspace collection or connected backend ──
export const ExtSourceRow = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  width: '100%',
  textAlign: 'left',
  padding: 'Y Z',
  round: 'radiusControl',
  borderWidth: '0',
  background: 'transparent',
  color: 'subtitle',
  cursor: 'pointer',
  ':hover': { background: 'hover', color: 'title' },
  isActive: (el, s) => (s.root?._extPath || el.call('makeRemoteUrl')) === s.target,
  '.isActive': { background: 'selected', color: 'title' },
  onClick: (e, el, s) => {
    e.preventDefault()
    el.call('extSelectSource', { slug: s.slug, table: s.table || undefined })
  },

  Chip: {
    align: 'center center',
    width: 'A1',
    height: 'A1',
    minWidth: 'A1',
    round: 'radiusControl',
    background: 'overlayStrong',
    color: 'title',
    overflow: 'hidden',
    Icon: {
      hide: (el, s) => !s.kindIcon || !el.call('hasIntegrationLogo', s.kindIcon),
      name: (el, s) => s.kindIcon || 'settings',
      width: '60%',
      height: '60%'
    },
    Letter: {
      hide: (el, s) => !!(s.kindIcon && el.call('hasIntegrationLogo', s.kindIcon)),
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      text: (el, s) => ((s.label || '?')[0] || '?').toUpperCase()
    }
  },

  Meta: {
    flow: 'y',
    gap: 'V',
    flex: '1 1 0',
    minWidth: '0',
    Label: {
      tag: 'span',
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'currentColor',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      text: (el, s) => s.label || ''
    },
    Sub: {
      hide: (el, s) => !s.sub,
      tag: 'span',
      fontSize: 'Y1',
      color: 'caption',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      text: (el, s) => s.sub || ''
    }
  },

  Count: {
    hide: (el, s) => typeof s.count !== 'number',
    tag: 'span',
    padding: 'V Z',
    round: 'radiusPill',
    background: 'overlayStrong',
    color: 'caption',
    fontSize: 'Y1',
    fontWeight: '700',
    text: (el, s) => String(s.count ?? '')
  },

  // Dispatchable dot — green when the server can actually run capability
  // calls for this kind today, dim when it's connect-only.
  Dot: {
    hide: (el, s) => !s.showDot,
    width: 'Y',
    height: 'Y',
    minWidth: 'Y',
    round: '100%',
    background: (el, s) => (s.dispatchable ? 'green' : 'caption'),
    opacity: (el, s) => (s.dispatchable ? '1' : '0.4'),
    title: (el, s) =>
      el.call('polyglot', s.dispatchable ? 'data.external.dot.live' : 'data.external.dot.connected')
  }
}

// ── Declared-capability chip ──
export const ExtCapChip = {
  tag: 'span',
  padding: 'V Z',
  round: 'radiusPill',
  background: 'overlayStrong',
  color: 'caption',
  fontSize: 'Y1',
  fontWeight: '700',
  fontFamily: 'mono',
  text: (el, s) => s.cap || ''
}

// ── Schema field pill (records field defs + derived instantdb columns) ──
export const ExtFieldPill = {
  flow: 'x',
  align: 'center left',
  gap: 'Y',
  padding: 'V Z',
  round: 'radiusPill',
  // Redundant border removed (border-habit sweep, design-direction.md §8) —
  // matches this same file's ScopePill/StatusPill/ExtCapChip, all fill-only
  // pill chips with no border.
  background: 'overlay',
  fontSize: 'Y1',
  Name: {
    tag: 'span',
    fontFamily: 'mono',
    fontWeight: '700',
    color: 'title',
    text: (el, s) => s.name || ''
  },
  Kind: {
    tag: 'span',
    color: 'caption',
    text: (el, s) => {
      const base = s.format || s.type || ''
      if (s.is_primary_key) return base ? `${base} · PK` : 'PK'
      return s.nullable === false ? `${base} *` : base
    }
  }
}
